import { forwardRef, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import {
  ApiManualSensor,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import { Button, Stack } from '@mui/material';
import DeviceService from '../../../../api/services/config/device/DeviceService';
import { getMeasureTypesFromDeviceType } from '../../../../store/config';
import { resetExternalState } from '../../../../store/data/slice';
import { DataState } from '../../../../store/data/state';
import { useAppDispatch } from '../../../../store/hooks';
import { createValidator, useForm } from '../../../../utils/hooks/useForm';
import TextInput from '../../../utils/MUISpecifics/inputText/TextInput';
import DropdownItemsSelector from '../../../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import DropdownStringSelector from '../../../utils/MUISpecifics/dropDownStringSelector/DropDownStringSelector';
import CustomizedDialog from '../../../utils/MUISpecifics/dialog/CustumizedDialog';
import { useSubmit } from '../../../../utils/hooks/useSubmit';
import { useTranslation } from 'react-i18next';

interface Step1Props {
  onCancel: () => void;
  isOpen: boolean;
  closeModal: () => void;
  deviceTypeMeasureTypes: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >;
  device_types: ApiReturnDeviceType[];
  measureTypes: DataState<string, { [KEY in number]: ApiReturnMeasureType }>;
  models: string[];
}

const defaultValues: ApiManualSensor = {
  name: '',
  brand: '',
  type: 'manual_sensor',
  device_type_id: null,
  sn: null,
};

function Step1({
  onCancel,
  isOpen,
  closeModal,
  deviceTypeMeasureTypes,
  device_types,
  measureTypes,
  models,
}: Step1Props): JSX.Element {
  // use translation here:
  const { t } = useTranslation('components.devices');

  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const [filtredProposition, setFiltredProposition] = useState<{
    deviceType: ApiReturnDeviceType | null;
    measureTypes: ApiReturnMeasureType[];
  }>({ deviceType: null, measureTypes: [] });

  const getDeviceTypeById = (id: number) => {
    return device_types.find((dt) => dt.id === id);
  };

  const updateMeasures = (id: number) => {
    if (id !== null) {
      const deviceType = getDeviceTypeById(id);
      const measures = getMeasureTypesFromDeviceType(
        deviceType!,
        deviceTypeMeasureTypes,
        measureTypes
      );
      methods.setValue('device_type_id', id);
      setFiltredProposition({
        deviceType: deviceType!,
        measureTypes: measures,
      });
    } else {
      methods.setValue('device_type_id', defaultValues.device_type_id);
      setFiltredProposition({ deviceType: null, measureTypes: [] });
    }
  };

  /**
   * Create validation rule for each field.
   */
  const validator = createValidator<ApiManualSensor>({
    name: [
      (n) =>
        (n && n.length <= 3) || !n.trim().length
          ? t('Entrer au moins 4 caractères')
          : undefined,
    ],
    brand: [
      (n) =>
        (n && n === '') || n === null || n === undefined || !n.trim().length
          ? t('Choisir un modèle ou entrer des caractères valides')
          : undefined,
    ],
    type: [
      (n) =>
        n && n.length <= 3 ? t('Entrer au moins 4 caractères') : undefined,
    ],
    sn: [
      (n) =>
        n === null || n === '' ? t('Entrer au moins 4 caractères') : undefined,
      (n) =>
        (n && n !== null && n.length <= 3) ||
        (n && n !== null && !n.trim().length)
          ? t('Entrer au moins 4 caractères')
          : undefined,
    ],
    device_type_id: [
      (n) => (n === null ? t('Choisir un type de capteur') : undefined),
    ],
  });

  const onSubmit = async (data: ApiManualSensor) => {
    const measureTypesToInsert = filtredProposition.measureTypes
      ? filtredProposition.measureTypes.map((mt) => mt.id)
      : [];

    let response: number | null = null;

    submit({
      callbacks: [
        async () => {
          response = await DeviceService.add(data);
        },
        async () => {
          if (!response)
            throw new Error('Échec de la création du dispositif de mesure');
          await DeviceService.associateMeasureTypesToDevice(
            measureTypesToInsert,
            response // response from the first callback
          );
        },
        () => dispatch(resetExternalState({ external_key: 'devices' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
      ],

      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la création du dispositif de mesure'
        ),
        unicityError: t(
          'Un dispositif de mesure avec le même modèle et numéro de série existe déjà'
        ),
      },
      successMessage: t('Le dispositif de mesure a été créé avec succès'),
    });
  };

  const methods = useForm(defaultValues, validator, onSubmit);

  return (
    <div
      style={{
        padding: '3em',
        border: '1px solid #ccc',
        borderRadius: '0.3em',
      }}
    >
      <CustomizedDialog
        title={t('Ajouter un nouveau dispositif de mesure')}
        open={isOpen}
        onCancel={onCancel}
        titleSx={{
          marginRight: '2em',
        }}
      >
        <form onSubmit={methods.onSubmit} className="form" noValidate>
          <Stack spacing={2}>
            <TextInput
              onChange={(value) => methods.setValue('name', value)}
              label={t('Nom')}
              placeholder={t('Entrer un nom...')}
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
              name={''}
            />
            <DropdownItemsSelector
              onSelect={(value) => updateMeasures(value!)}
              values={device_types}
              label={t('Type de dispositif de mesure')}
              disabled={false}
              name={'device_type_id'}
              required
              errors={
                methods.hasSubmitted
                  ? {
                      tag: 'Submited',
                      errors: methods.getErrors('device_type_id'),
                    }
                  : { tag: 'Not submitted' }
              }
            />
            {filtredProposition.measureTypes.length !== 0 && (
              <div
                className="uk-margin"
                style={{
                  backgroundColor: '#00000008',
                  borderRadius: '5px',
                  padding: '15px',
                }}
              >
                <div>Types de mesures</div>
                <div
                  className="uk-margin"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  {filtredProposition.measureTypes.map((mt) => (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'start',
                        gap: '10px',
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: '#d3f7b0',
                          padding: '5px',
                          borderRadius: '5px',
                        }}
                      >
                        {mt.name}
                      </span>
                      <span
                        style={{
                          backgroundColor: '#f7b0ba',
                          padding: '5px',
                          borderRadius: '5px',
                        }}
                      >
                        {mt.unite}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DropdownStringSelector
              onSelect={(value) => methods.setValue('brand', value)}
              values={models}
              label={t('Modèle')}
              disabled={false}
              name={'device_type_id'}
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('brand') }
                  : { tag: 'Not submitted' }
              }
            />
            <TextInput
              name={'sn'}
              onChange={(value) => methods.setValue('sn', value)}
              label={t('Numéro de série')}
              placeholder={t('Entrer un numéro de série...')}
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('sn') }
                  : { tag: 'Not submitted' }
              }
            />
            <Button
              variant="outlined"
              loadingPosition="end"
              endIcon={<SaveIcon />}
              type="submit"
              sx={{ width: '10rem', alignSelf: 'center' }}
            >
              {t('Enregistrer')}
            </Button>
          </Stack>
        </form>
      </CustomizedDialog>
    </div>
  );
}

export default forwardRef(Step1);
